<template>
    <div>
        <div class="foxy-card transform overflow-hidden bg-foxy-surface/80 backdrop-blur-xl hover:-translate-y-1">
            <div class="p-4">
                <div class="flex space-x-4">
                    <!-- Book Cover -->
                    <div class="flex-shrink-0">
                        <a
                            :href="bookUrl"
                            class="book-cover-frame block rounded-lg shadow-md transition-shadow duration-300 hover:shadow-lg"
                            style="width: 120px; height: auto; aspect-ratio: 2 / 3"
                        >
                            <img
                                :src="book.cover_image || bookCoverPlaceholder"
                                :alt="book.title"
                                :loading="loadPriority ? 'eager' : 'lazy'"
                                :fetchpriority="loadPriority ? 'high' : undefined"
                                decoding="async"
                                width="120"
                                height="180"
                                class="book-cover-media"
                                @error="onCoverError"
                            />
                        </a>
                    </div>

                    <!-- Book Info -->
                    <div class="flex h-auto w-full flex-col justify-between">
                        <div>
                            <h3
                                class="mb-1 line-clamp-2 min-h-[3.25rem] text-lg font-bold leading-snug text-foxy-text transition-colors group-hover:text-foxy-action"
                            >
                                <a :href="bookUrl">{{ displayTitle }}</a>
                            </h3>
                            <p class="mb-2 text-sm font-medium text-foxy-text-secondary">{{ book.author }}</p>

                            <!-- Rating -->
                            <div v-if="!hideUnratedRating || hasRating" class="mb-2 flex items-center">
                                <div class="flex items-center">
                                    <template v-for="i in 5">
                                        <svg
                                            class="h-3 w-3"
                                            :class="i <= visualStarRating ? 'text-foxy-honey' : 'text-foxy-border'"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                                            />
                                        </svg>
                                    </template>
                                </div>
                                <span class="ml-2 text-sm font-medium text-foxy-text-secondary">{{ formattedRating || '—' }}</span>
                                <span class="ml-1 text-sm text-foxy-text-secondary/75">({{ ratingCount }})</span>
                            </div>
                        </div>

                        <!-- Stats and Button -->
                        <div class="flex w-full justify-end">
                            <div class="align-center flex w-full justify-between">
                                <div class="flex items-center space-x-4">
                                    <div class="flex items-center space-x-2 text-foxy-text-secondary" v-if="book.reviews_count > 0">
                                        <i class="fas fa-feather"></i>
                                        <span class="text-base font-semibold">{{ book.reviews_count }}</span>
                                    </div>
                                </div>
                            </div>
                            <!-- Add Button -->
                            <div v-if="currentStatus && isAuthenticated" class="flex items-center gap-2">
                                <button
                                    @click="openReadingStatusModal"
                                    class="inline-flex items-center justify-center whitespace-nowrap rounded-xl px-3 py-2 text-xs font-semibold shadow-lg transition-all duration-300 hover:opacity-90"
                                    :class="getStatusButtonClass(currentStatus)"
                                >
                                    <span class="text-white">{{ statusTexts[currentStatus] }}</span>
                                </button>
                                <button
                                    @click="openReadingStatusModal"
                                    class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foxy-action text-white shadow-lg transition-all duration-200 hover:bg-foxy-action/90"
                                >
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                </button>
                            </div>
                            <button
                                v-else-if="isAuthenticated"
                                @click="openReadingStatusModal"
                                class="foxy-btn-primary transform !py-2 shadow-lg hover:scale-105"
                            >
                                <div class="flex items-center space-x-2">
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    <span class="text-sm">Додати</span>
                                </div>
                            </button>
                            <a v-else :href="loginUrl" class="foxy-btn-primary transform !py-2 shadow-lg hover:scale-105">
                                <div class="flex items-center space-x-2">
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    <span class="text-sm">Додати</span>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Reading Status Modal -->
        <add-to-library-modal
            v-if="isAuthenticated"
            :show="showModal"
            :book="book"
            :user-libraries="userLibraries"
            :current-status="currentStatus"
            @close="closeModal"
            @status-selected="handleStatusSelected"
            @remove-status="handleRemoveStatus"
            @open-custom-library="openCustomLibraryModal"
            @notification="$emit('notification', $event)"
        />

        <!-- Custom Library Modal -->
        <div v-if="showCustomLibraryModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div
                class="foxy-modal mx-4 max-w-md transform transition-all duration-300"
                :class="{ 'scale-100 opacity-100': showCustomLibraryModal, 'scale-95 opacity-0': !showCustomLibraryModal }"
            >
                <div class="p-6">
                    <div class="mb-6 flex items-center justify-between">
                        <h3 class="text-2xl font-bold text-foxy-text">Додати до добірки</h3>
                        <button @click="closeCustomLibraryModal" class="foxy-icon-button !h-6 !w-6">
                            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <!-- Список существующих добірок, где уже есть книга -->
                    <div v-if="currentBookLibraries.length > 0" class="mb-6">
                        <label class="mb-3 block text-sm font-medium text-foxy-text-secondary"> Книга вже в добірках: </label>
                        <div class="space-y-2">
                            <div
                                v-for="library in currentBookLibraries"
                                :key="library.id"
                                class="flex items-center justify-between rounded-xl bg-foxy-elevated p-3"
                            >
                                <div class="flex items-center space-x-2">
                                    <i class="fas fa-folder text-foxy-brand"></i>
                                    <span class="font-medium text-foxy-text">{{ library.name }}</span>
                                    <span v-if="library.is_private" class="text-xs text-foxy-text-secondary">(Приватна)</span>
                                    <span v-else class="text-xs text-foxy-text-secondary">(Публічна)</span>
                                </div>
                                <button
                                    @click="removeFromLibrary(library)"
                                    class="p-1 text-red-500 transition-colors hover:text-red-700 dark:hover:text-red-400"
                                    title="Видалити з добірки"
                                >
                                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <form @submit.prevent="submitToCustomLibrary">
                        <div class="mb-6">
                            <label class="mb-3 block text-sm font-medium text-foxy-text-secondary">Оберіть добірку</label>
                            <select v-model="selectedLibraryId" required class="foxy-select">
                                <option value="">-- Оберіть добірку --</option>
                                <option v-for="library in availableLibraries" :key="library.id" :value="library.id">
                                    {{ library.name }}
                                    <span v-if="library.is_private">(Приватна)</span>
                                    <span v-else>(Публічна)</span>
                                </option>
                            </select>
                        </div>

                        <div class="flex space-x-3">
                            <button type="submit" class="foxy-btn-primary flex-1 transform !px-6 !py-3 hover:scale-105">Додати</button>
                            <button type="button" @click="closeCustomLibraryModal" class="foxy-btn-secondary flex-1 !px-6 !py-3">Скасувати</button>
                        </div>
                    </form>

                    <div v-if="userLibraries.length === 0" class="mt-4 rounded-xl bg-foxy-elevated p-4">
                        <p class="mb-3 text-sm text-foxy-text-secondary">У вас ще немає добірок</p>
                        <a :href="profileCollectionsUrl" class="text-sm font-medium text-foxy-action hover:text-foxy-action/80">
                            Створити першу добірку →
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { requestLibraryRemoval } from '../utils/libraryRemoval';
const { formatTenPointRating, hasPresentableRating, ratingCountForDisplay, toFiveStarRating } = require('../utils/bookRatingPresentation');

export default {
    name: 'BookCard',
    props: {
        book: {
            type: Object,
            required: true,
        },
        useCanonicalDisplayTitle: {
            type: Boolean,
            default: false
        },
        loadPriority: {
            type: Boolean,
            default: false,
        },
        hideUnratedRating: {
            type: Boolean,
            default: false,
        },
        userLibraries: {
            type: Array,
            default: () => [],
        },
        isAuthenticated: {
            type: Boolean,
            default: false,
        },
        user: {
            type: Object,
            default: null,
        },
    },
    data() {
        // Ініціалізуємо статус синхронно з кешу для миттєвого відображення
        let initialStatus = null;
        if (this.isAuthenticated && this.book && this.book.id && window.bookStatusCache) {
            const cachedStatus = window.bookStatusCache.get(this.book.id);
            if (cachedStatus && cachedStatus.status) {
                initialStatus = cachedStatus.status;
            }
        }

        return {
            showModal: false,
            showCustomLibraryModal: false,
            selectedLibraryId: '',
            currentStatus: initialStatus,
            readingStatusLoaded: false,
            readingStatusLockVersion: null,
            openReadingInstanceId: null,
            openReadingState: null,
            currentBookLibraries: [],
            statusTexts: {
                read: 'Прочитано',
                reading: 'Читаю',
                want_to_read: 'Планую',
                abandoned: 'Не дочитано',
            },
        };
    },
    computed: {
        displayTitle() {
            return this.useCanonicalDisplayTitle ? this.book.title : (this.book.book_name_ua || this.book.title);
        },
        hasRating() {
            return hasPresentableRating(this.book);
        },
        formattedRating() {
            return formatTenPointRating(this.book);
        },
        ratingCount() {
            return ratingCountForDisplay(this.book);
        },
        visualStarRating() {
            return toFiveStarRating(this.book.rating);
        },
        bookCoverPlaceholder() {
            return window.imagePlaceholders && window.imagePlaceholders.bookCover
                ? window.imagePlaceholders.bookCover
                : '/images/placeholders/book-cover.svg';
        },
        bookUrl() {
            return `/books/${this.book.slug}`;
        },
        loginUrl() {
            return '/login';
        },
        profileCollectionsUrl() {
            // Получаем текущего пользователя из глобальных данных Vue или window
            if (this.user && this.user.username) {
                return `/users/${this.user.username}/collections`;
            }
            return '#';
        },
        availableLibraries() {
            // Доборки, в которых книга еще не добавлена
            const bookLibraryIds = this.currentBookLibraries.map((lib) => lib.id);
            return this.userLibraries.filter((lib) => !bookLibraryIds.includes(lib.id));
        },
    },
    created() {
        // Миттєво завантажуємо статус з кешу для першого кадру
        if (this.isAuthenticated && this.book && this.book.id && window.bookStatusCache) {
            const cachedStatus = window.bookStatusCache.get(this.book.id);
            if (cachedStatus && cachedStatus.status) {
                this.currentStatus = cachedStatus.status;
            }
        }
    },
    mounted() {
        if (this.isAuthenticated) {
            // Завжди перевіряємо на сервері, щоб статуси після видалення в кабінеті синхронізувались
            this.loadReadingStatus();
            this.loadBookLibraries();
        }
    },
    methods: {
        applyCanonicalReadingSnapshot(snapshot) {
            if (!snapshot || typeof snapshot !== 'object' || !Object.prototype.hasOwnProperty.call(snapshot, 'status')) {
                throw new Error('Canonical reading status is missing from the server response.');
            }

            this.currentStatus = snapshot.status ?? null;
            this.readingStatusLockVersion = snapshot.lock_version ?? null;
            this.openReadingInstanceId = snapshot.open_reading_instance_id ?? null;
            this.openReadingState = snapshot.open_reading_state ?? null;
            this.readingStatusLoaded = true;

            const bookId = this.book?.id;
            if (bookId && window.bookStatusCache) {
                if (this.currentStatus) {
                    window.bookStatusCache.set(bookId, {
                        status: this.currentStatus,
                        times_read: snapshot.times_read ?? 0,
                        reading_language: snapshot.reading_language ?? null,
                        rating: snapshot.rating ?? null,
                        review: snapshot.review ?? null,
                        started_at: snapshot.started_at ?? null,
                        finished_at: snapshot.finished_at ?? null,
                    });
                } else {
                    window.bookStatusCache.remove(bookId);
                }
            }
        },
        onCoverError(event) {
            // Replace broken image with placeholder (avoid infinite loop)
            const fallback = this.bookCoverPlaceholder;
            if (event?.target?.src && event.target.src !== fallback) {
                event.target.src = fallback;
            }
        },
        openReadingStatusModal() {
            console.log('Opening modal, isAuthenticated:', this.isAuthenticated);
            console.log('Book:', this.book);
            this.showModal = true;
        },
        closeModal() {
            this.showModal = false;
        },
        async handleStatusSelected(status) {
            try {
                // Використовуємо ID з об'єкта книги, якщо він є
                const bookId = this.book.id || (await this.getBookIdBySlug(this.book.slug));

                // Отправляем запрос на сервер
                const normalizedStatus = status === 'want-to-read' ? 'want_to_read' : status;
                const response = await axios.post(`/api/reading-status/book/${bookId}`, {
                    status: normalizedStatus,
                });

                if (response.data.success) {
                    this.applyCanonicalReadingSnapshot({
                        ...response.data.data,
                        open_reading_instance_id: response.data.open_reading_instance_id ?? null,
                        open_reading_state: response.data.open_reading_state ?? null,
                    });

                    this.showAlert('Книга додана до списку!', 'Успіх', 'success');
                } else {
                    this.showAlert(response.data.message || 'Помилка при збереженні статусу', 'Помилка', 'error');
                }
            } catch (error) {
                console.error('Error:', error);
                this.showAlert('Помилка при збереженні статусу', 'Помилка', 'error');
            }

            this.closeModal();
        },
        async handleRemoveStatus() {
            try {
                // Використовуємо ID з об'єкта книги, якщо він є
                const bookId = this.book.id || (await this.getBookIdBySlug(this.book.slug));
                if (!this.readingStatusLoaded) {
                    await this.loadReadingStatus();
                }
                const result = await requestLibraryRemoval((payload) => axios.delete(`/api/reading-status/book/${bookId}`, { data: payload }), {
                    lockVersion: this.readingStatusLockVersion,
                    openReadingInstanceId: this.openReadingInstanceId,
                    openReadingState: this.openReadingState,
                });
                if (result.stale) {
                    this.showAlert('Статус читання змінився. Дані книги оновлено.', 'Потрібне оновлення', 'warning');
                    await this.loadReadingStatus();
                    return;
                }
                if (!result.completed) return;

                const response = result.response;

                if (response.data && response.data.success) {
                    this.currentStatus = result.status;
                    this.readingStatusLockVersion = response.data.lock_version ?? null;
                    this.openReadingInstanceId = null;
                    this.openReadingState = null;
                    this.readingStatusLoaded = true;

                    if (window.bookStatusCache) {
                        if (result.status) {
                            window.bookStatusCache.set(bookId, { status: result.status });
                        } else {
                            window.bookStatusCache.remove(bookId);
                        }
                    }

                    this.showAlert(response.data.message, 'Успіх', 'success');
                } else {
                    this.showAlert(response.data?.message || 'Помилка при видаленні статусу', 'Помилка', 'error');
                }
            } catch (error) {
                console.error('Error:', error);
                this.showAlert('Помилка при видаленні статусу', 'Помилка', 'error');
            }

            this.closeModal();
        },
        async loadReadingStatus() {
            if (!this.isAuthenticated) return;

            // Використовуємо ID з об'єкта книги, якщо він є
            let bookId = this.book.id;

            // Якщо ID немає, отримуємо його (з кешу або сервера)
            if (!bookId) {
                bookId = await this.getBookIdBySlug(this.book.slug);
                if (!bookId) return;
            }

            // Always verify with server (do not trust stale localStorage)
            try {
                const response = await axios.get(`/api/reading-status/book/${bookId}`, {
                    timeout: 5000, // Таймаут 5 секунд
                });

                if (response.data) {
                    const statusData = {
                        status: response.data.status,
                        times_read: response.data.times_read,
                        reading_language: response.data.reading_language,
                        rating: response.data.rating,
                        review: response.data.review,
                        started_at: response.data.started_at,
                        finished_at: response.data.finished_at,
                    };

                    // Оновлюємо кеш
                    if (statusData.status && window.bookStatusCache) {
                        window.bookStatusCache.set(bookId, statusData);
                        this.currentStatus = statusData.status;
                        this.readingStatusLockVersion = response.data.lock_version ?? null;
                        this.openReadingInstanceId = response.data.open_reading_instance_id ?? null;
                        this.openReadingState = response.data.open_reading_state ?? null;
                        this.readingStatusLoaded = true;
                    } else {
                        // Якщо статусу немає, видаляємо з кешу
                        if (window.bookStatusCache) {
                            window.bookStatusCache.remove(bookId);
                        }
                        this.currentStatus = null;
                        this.readingStatusLockVersion = null;
                        this.openReadingInstanceId = null;
                        this.openReadingState = null;
                        this.readingStatusLoaded = true;
                    }
                }
            } catch (error) {
                // Ігноруємо помилки переривання запиту
                if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
                    return;
                }

                if (error.response && error.response.status === 404) {
                    if (window.bookStatusCache) {
                        window.bookStatusCache.remove(bookId);
                    }
                    this.currentStatus = null;
                    this.readingStatusLockVersion = null;
                    this.openReadingInstanceId = null;
                    this.openReadingState = null;
                    this.readingStatusLoaded = true;
                    return;
                }

                // Игнорируем ошибку, если статус не найден
                if (error.response && error.response.status !== 404) {
                    console.error('Error loading reading status:', error);
                }
            }
        },
        async loadBookLibraries() {
            if (!this.isAuthenticated) return;

            try {
                const response = await axios.get(`/api/books/${this.book.slug}/libraries`, {
                    timeout: 5000, // Таймаут 5 секунд
                });
                if (response.data && response.data.success) {
                    this.currentBookLibraries = response.data.libraries || [];
                }
            } catch (error) {
                // Ігноруємо помилки переривання запиту
                if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
                    return;
                }

                // Игнорируем ошибку, если библиотеки не найдены
                if (error.response && error.response.status !== 404) {
                    console.error('Error loading book libraries:', error);
                }
            }
        },
        async getBookIdBySlug(slug) {
            if (!slug) return null;

            // Спочатку перевіряємо кеш
            if (window.bookIdCache) {
                const cachedId = window.bookIdCache.get(slug);
                if (cachedId) {
                    return cachedId;
                }
            }

            try {
                const response = await axios.get(`/api/books/${slug}/id`, {
                    timeout: 5000, // Таймаут 5 секунд
                });

                if (response.data && response.data.id) {
                    // Зберігаємо в кеш
                    if (window.bookIdCache) {
                        window.bookIdCache.set(slug, response.data.id);
                    }
                    return response.data.id;
                }

                return null;
            } catch (error) {
                // Ігноруємо помилки переривання запиту (коли компонент розмонтовується)
                if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
                    return null;
                }

                // Інші помилки логуємо тільки якщо це не 404
                if (error.response && error.response.status !== 404) {
                    console.error('Error getting book ID:', error);
                }
                return null;
            }
        },
        async openCustomLibraryModal() {
            this.showModal = false; // Закрываем модальное окно статусов
            await this.loadBookLibraries(); // Загружаем актуальный список библиотек
            this.showCustomLibraryModal = true; // Открываем модальное окно кастомных библиотек
        },
        closeCustomLibraryModal() {
            this.showCustomLibraryModal = false;
            this.selectedLibraryId = ''; // Сбрасываем выбранную библиотеку
        },
        async submitToCustomLibrary() {
            if (!this.selectedLibraryId) {
                this.showAlert('Оберіть добірку', 'Помилка', 'error');
                return;
            }

            try {
                const url = `/libraries/${this.selectedLibraryId}/add-book`;
                const formData = new FormData();
                formData.append('book_slug', this.book.slug);

                const response = await axios.post(url, formData);

                if (response.data.success) {
                    await this.loadBookLibraries(); // Обновляем список библиотек
                    this.showAlert('Книга успішно додана до добірки!', 'Успіх', 'success');
                    this.selectedLibraryId = '';
                } else {
                    this.showAlert(response.data.message || 'Помилка при додаванні книги', 'Помилка', 'error');
                }
            } catch (error) {
                console.error('Error:', error);
                this.showAlert('Помилка при додаванні книги', 'Помилка', 'error');
            }
        },
        async removeFromLibrary(library) {
            try {
                const response = await axios.delete(`/libraries/${library.id}/books/${this.book.id}`);

                if (response.data.success) {
                    this.currentBookLibraries = this.currentBookLibraries.filter((lib) => lib.id !== library.id);
                    this.showAlert(`Книгу видалено з добірки "${library.name}"`, 'Успіх', 'success');
                } else {
                    this.showAlert(response.data.message || 'Помилка при видаленні книги', 'Помилка', 'error');
                }
            } catch (error) {
                console.error('Error:', error);
                this.showAlert(error.response?.data?.message || 'Помилка при видаленні книги', 'Помилка', 'error');
            }
        },
        getStatusButtonClass(status) {
            const classes = {
                read: 'bg-green-500 hover:bg-green-600 text-white',
                reading: 'bg-blue-500 hover:bg-blue-600 text-white',
                want_to_read: 'bg-purple-500 hover:bg-purple-600 text-white',
                abandoned: 'bg-red-500 hover:bg-red-600 text-white',
            };
            return classes[status] || classes['want_to_read'];
        },
        showAlert(message, title, type = 'info') {
            if (window.alertModalInstance && window.alertModalInstance.$refs && window.alertModalInstance.$refs.modal) {
                window.alertModalInstance.$refs.modal.alert(message, title, type);
            } else {
                // Fallback если модалка не готова
                setTimeout(() => {
                    if (window.alertModalInstance && window.alertModalInstance.$refs && window.alertModalInstance.$refs.modal) {
                        window.alertModalInstance.$refs.modal.alert(message, title, type);
                    }
                }, 100);
            }
        },
    },
};
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
